import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";
import type {
  DirectQuoteRequest,
  DirectQuoteResponse,
  DirectQuoteLoadRow,
} from "@/hooks/useDirectQuoteRequests";

/**
 * Broker Quote Requests — SERVER-SIDE paginated (LIMIT/OFFSET via PostgREST
 * `.range()`), one page per tab. Totals come from `count: "exact", head: true`
 * so the tab badges stay accurate without fetching every row.
 *
 * Ordering per tab (stable): LR Direct quote posts first (posted_at desc, id
 * desc), then private-network RFQs (deadline desc, id desc) — same visual
 * order the page rendered before.
 */
export type BrokerQuoteTab = "open" | "history";

const REQUEST_COLS = "*";
const LOAD_COLS =
  "id, origin_city, origin_state, origin_postal_code, destination_city, destination_state, destination_postal_code, equipment_type, weight_lbs, commodity, pickup_date, bid_deadline, shipper_notes_to_brokers, target_rate_min, target_rate_max, visibility_mode, status, created_at, posted_at";

export function useBrokerQuoteRequestsPaged(
  userId: string | undefined,
  tab: BrokerQuoteTab,
  page: number,
  pageSize = 10,
) {
  const [loads, setLoads] = useState<DirectQuoteLoadRow[]>([]);
  const [requests, setRequests] = useState<DirectQuoteRequest[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [mineByReq, setMineByReq] = useState<Map<string, DirectQuoteResponse>>(new Map());
  const [mineByLoad, setMineByLoad] = useState<Map<string, DirectQuoteResponse>>(new Map());
  const [openTotal, setOpenTotal] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const runIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!userId) {
      setLoads([]); setRequests([]); setHiddenIds(new Set());
      setMineByReq(new Map()); setMineByLoad(new Map());
      setOpenTotal(0); setHistoryTotal(0); setLoading(false);
      return;
    }
    const runId = ++runIdRef.current;
    setLoading(true);
    const nowIso = new Date().toISOString();

    // Broker-scoped hide list (tiny table) — needed for the open/history split.
    const { data: hiddenRows } = await supabase
      .from("direct_quote_request_broker_hidden")
      .select("request_id")
      .eq("broker_user_id", userId);
    const hidden = (hiddenRows ?? []).map((h: any) => h.request_id as string);
    const hiddenSet = new Set<string>(hidden);

    const reqBase = () =>
      supabase.from("direct_quote_requests").select(REQUEST_COLS, { count: "exact" })
        .in("status", ["open", "closed", "expired"]);
    const reqCount = () =>
      supabase.from("direct_quote_requests").select("id", { count: "exact", head: true })
        .in("status", ["open", "closed", "expired"]);
    const loadBase = () =>
      supabase.from("direct_loads" as any).select(LOAD_COLS, { count: "exact" })
        .eq("post_type", "quote").is("deleted_at", null);
    const loadCount = () =>
      supabase.from("direct_loads" as any).select("id", { count: "exact", head: true })
        .eq("post_type", "quote").is("deleted_at", null);

    const applyReqOpen = (q: any) => {
      let out = q.eq("status", "open").is("archived_at", null).gt("deadline", nowIso);
      if (hidden.length) out = out.not("id", "in", `(${hidden.join(",")})`);
      return out;
    };
    const applyReqHistory = (q: any) => {
      const clauses = [
        "status.in.(closed,expired)",
        "archived_at.not.is.null",
        `deadline.lte.${nowIso}`,
      ];
      if (hidden.length) clauses.push(`id.in.(${hidden.join(",")})`);
      return q.or(clauses.join(","));
    };
    const applyLoadOpen = (q: any) =>
      q.eq("status", "posted").or(`bid_deadline.is.null,bid_deadline.gt.${nowIso}`);
    const applyLoadHistory = (q: any) =>
      q.or(`status.neq.posted,bid_deadline.lte.${nowIso}`);

    // ── Totals for both tabs (head-only counts) ──────────────────────────
    const [rOpenC, rHistC, lOpenC, lHistC] = await Promise.all([
      applyReqOpen(reqCount()),
      applyReqHistory(reqCount()),
      applyLoadOpen(loadCount()),
      applyLoadHistory(loadCount()),
    ]);
    if (runId !== runIdRef.current) return;

    const openCount = (rOpenC.count ?? 0) + (lOpenC.count ?? 0);
    const histCount = (rHistC.count ?? 0) + (lHistC.count ?? 0);
    setOpenTotal(openCount);
    setHistoryTotal(histCount);

    const loadTotal = (tab === "open" ? lOpenC.count : lHistC.count) ?? 0;
    const offset = Math.max(0, (page - 1) * pageSize);

    // Slice math across the two concatenated sources.
    const loadOffset = Math.min(offset, loadTotal);
    const loadTake = Math.max(0, Math.min(pageSize, loadTotal - loadOffset));
    const reqOffset = Math.max(0, offset - loadTotal);
    const reqTake = pageSize - loadTake;

    let loadRows: DirectQuoteLoadRow[] = [];
    let reqRows: DirectQuoteRequest[] = [];

    const jobs: Promise<any>[] = [];
    if (loadTake > 0) {
      let q = tab === "open" ? applyLoadOpen(loadBase()) : applyLoadHistory(loadBase());
      q = q
        .order("posted_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .range(loadOffset, loadOffset + loadTake - 1);
      jobs.push(q.then((res: any) => { loadRows = (res.data ?? []) as DirectQuoteLoadRow[]; }));
    }
    if (reqTake > 0) {
      let q = tab === "open" ? applyReqOpen(reqBase()) : applyReqHistory(reqBase());
      q = q
        .order("deadline", { ascending: false })
        .order("id", { ascending: false })
        .range(reqOffset, reqOffset + reqTake - 1);
      jobs.push(q.then((res: any) => {
        if (res.error) setError(res.error.message);
        reqRows = (res.data ?? []) as DirectQuoteRequest[];
      }));
    }
    await Promise.all(jobs);
    if (runId !== runIdRef.current) return;

    // ── This broker's own responses, scoped to the visible page only ─────
    const pageReqIds = reqRows.map((r) => r.id);
    const pageLoadIds = loadRows.map((l) => l.id);
    const byReq = new Map<string, DirectQuoteResponse>();
    const byLoad = new Map<string, DirectQuoteResponse>();
    if (pageReqIds.length || pageLoadIds.length) {
      let rq = supabase.from("direct_quote_responses").select("*").eq("broker_user_id", userId);
      if (pageReqIds.length && pageLoadIds.length) {
        rq = rq.or(
          `request_id.in.(${pageReqIds.join(",")}),direct_load_id.in.(${pageLoadIds.join(",")})`,
        );
      } else if (pageReqIds.length) {
        rq = rq.in("request_id", pageReqIds);
      } else {
        rq = rq.in("direct_load_id", pageLoadIds);
      }
      const { data: mine } = await rq;
      (mine ?? []).forEach((r: any) => {
        if (r.request_id) byReq.set(r.request_id, r as DirectQuoteResponse);
        else if (r.direct_load_id) byLoad.set(r.direct_load_id, r as DirectQuoteResponse);
      });
    }
    if (runId !== runIdRef.current) return;

    setLoads(loadRows);
    setRequests(reqRows);
    setHiddenIds(hiddenSet);
    setMineByReq(byReq);
    setMineByLoad(byLoad);
    setError(null);
    setLoading(false);
  }, [userId, tab, page, pageSize]);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime — same triggers as before, now refetching only the current page.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`broker-quote-paged-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_quote_responses" }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_loads" }, (payload) => {
        if ((payload.new as any)?.post_type === "quote" || (payload.old as any)?.post_type === "quote") refresh();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, refresh]);
  useRealtimeSelfHeal(refresh, !!userId);

  return {
    loads, requests, hiddenIds, mineByReq, mineByLoad,
    openTotal, historyTotal, loading, error, refresh,
  };
}
